import { parseHumanNumber } from '../../utils/numberInput'

type NormalizedFieldResult =
  | { ok: true; normalized: string }
  | { ok: false; error: string }

export type ValidateSamplingDraftInput = {
  averageWeightG: string
  dailyMortality: string
}

export type ValidateSamplingDraftResult = {
  normalizedAverageWeightG: string
  normalizedDailyMortality: string
}

type ValidateSamplingDraftOutcome =
  | { ok: true; value: ValidateSamplingDraftResult }
  | { ok: false; error: string }

const normalizeWholeNumberField = (
  rawValue: string,
  label: string,
  t: (key: any, vars?: Record<string, string | number>) => string
): NormalizedFieldResult => {
  const trimmed = String(rawValue ?? '').trim()
  if (trimmed === '') return { ok: true, normalized: '' }

  const parsed = parseHumanNumber(trimmed, { requireInteger: true })
  if (!parsed.ok) {
    return { ok: false, error: t('datatable.numericValidation.mustBeNumber', { label }) }
  }

  const numeric = parsed.value
  if (!Number.isInteger(numeric)) {
    return { ok: false, error: t('datatable.numericValidation.mustBeWholeNumber', { label }) }
  }

  return { ok: true, normalized: Math.round(numeric).toString() }
}

const normalizePositiveDecimalField = (
  rawValue: string,
  label: string,
  t: (key: any, vars?: Record<string, string | number>) => string
): NormalizedFieldResult => {
  const trimmed = String(rawValue ?? '').trim()
  if (trimmed === '') {
    return { ok: true, normalized: '' }
  }

  const parsed = parseHumanNumber(trimmed, { allowDecimal: true, requireInteger: false })
  if (!parsed.ok) {
    return { ok: false, error: t('biomass.error.mustBeNumber', { field: label }) }
  }

  if (parsed.value <= 0) {
    return { ok: false, error: t('biomass.error.mustBePositive', { field: label }) }
  }

  return { ok: true, normalized: parsed.value.toString() }
}

export const validateSamplingDraft = (
  input: ValidateSamplingDraftInput,
  t: (key: any, vars?: Record<string, string | number>) => string
): ValidateSamplingDraftOutcome => {
  const averageWeightLabel = t('biomass.field.avgWeightG')
  const dailyMortalityLabel = t('biomass.field.dailyMortality')

  const normalizedAverageWeight = normalizePositiveDecimalField(input.averageWeightG, averageWeightLabel, t)
  if (!normalizedAverageWeight.ok) return normalizedAverageWeight

  const normalizedDailyMortality = String(input.dailyMortality ?? '').trim()
    ? normalizeWholeNumberField(input.dailyMortality, dailyMortalityLabel, t)
    : { ok: true as const, normalized: '' }
  if (!normalizedDailyMortality.ok) return normalizedDailyMortality

  if (!normalizedAverageWeight.normalized && !normalizedDailyMortality.normalized) {
    return {
      ok: false,
      error: t('biomass.error.enterWeightOrMortality')
    }
  }

  return {
    ok: true,
    value: {
      normalizedAverageWeightG: normalizedAverageWeight.normalized,
      normalizedDailyMortality: normalizedDailyMortality.normalized
    }
  }
}
